import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, GripVertical, Search, Users2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { apiPersonManagers } from '../api/client.js';
import Avatar from '../components/Avatar';

/**
 * Sprint 14 — Vue tableau drag&drop de la Hiérarchie.
 *
 * Board groupé par manager : chaque section = un manager, les lignes = ses
 * collaborateurs directs. On **glisse** une ligne d'un manager vers un autre
 * pour réaffecter le collaborateur (remove ancien lien + assign nouveau).
 *
 * - ADMIN   : drag&drop actif (mutations `assign`/`remove`, adminRequired).
 * - MANAGER : lecture seule (pas de drag, pas de drop).
 *
 * Données : `GET /persons/hierarchy/overview` (1 appel, scope borné serveur).
 */

const UNMANAGED = '__none__';

export default function HierarchyTable({ canEdit, canOpenEdit = canEdit, onOpenPerson, onChanged }) {
  const [data, setData] = useState({ persons: [], edges: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [drag, setDrag] = useState(null); // { personId, fromManagerId }
  const [dropTarget, setDropTarget] = useState(null); // managerId | UNMANAGED
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set()); // sections repliées

  const toggleCollapse = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // `silent` : rechargement après mutation — ne repasse PAS par l'écran
  // "Chargement…" (qui démonterait le tableau et ferait remonter le scroll en
  // haut). On garde les données affichées, l'indicateur `busy` suffit.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiPersonManagers.overview();
      setData({ persons: res.persons ?? [], edges: res.edges ?? [] });
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const personById = useMemo(() => {
    const m = new Map();
    for (const p of data.persons) m.set(p.id, p);
    return m;
  }, [data.persons]);

  // Construit : sections = managers (ayant ≥1 report) + section "Sans manager".
  const { sections, unmanaged } = useMemo(() => {
    const reportsByManager = new Map(); // managerId -> personId[]
    const managedSet = new Set(); // personId ayant ≥1 manager
    for (const e of data.edges) {
      if (!reportsByManager.has(e.managerId)) reportsByManager.set(e.managerId, []);
      reportsByManager.get(e.managerId).push(e.managedId);
      managedSet.add(e.managedId);
    }
    const sec = [...reportsByManager.entries()]
      .map(([managerId, reportIds]) => ({
        manager: personById.get(managerId),
        managerId,
        reports: reportIds
          .map((id) => personById.get(id))
          .filter(Boolean)
          .sort((a, b) => `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`)),
      }))
      .filter((s) => s.manager) // manager hors périmètre → ignoré
      .sort((a, b) =>
        `${a.manager.lastName}${a.manager.firstName}`.localeCompare(
          `${b.manager.lastName}${b.manager.firstName}`,
        ),
      );
    const unm = data.persons.filter((p) => !managedSet.has(p.id));
    return { sections: sec, unmanaged: unm };
  }, [data.edges, data.persons, personById]);

  const matches = useCallback(
    (p) => {
      const q = query.toLowerCase().trim();
      if (!q) return true;
      return `${p.firstName} ${p.lastName} ${p.jobTitle ?? ''} ${p.roleName ?? ''} ${p.departmentName ?? ''}`
        .toLowerCase()
        .includes(q);
    },
    [query],
  );

  // ---------- Drag & drop ----------
  const onDragStart = (e, personId, fromManagerId) => {
    setDrag({ personId, fromManagerId });
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', personId);
    } catch {
      /* certains navigateurs exigent un setData */
    }
  };

  const onDrop = async (targetManagerId) => {
    const info = drag;
    setDropTarget(null);
    setDrag(null);
    if (!info) return;
    const { personId, fromManagerId } = info;
    if (targetManagerId === fromManagerId) return; // même groupe → no-op
    // Interdit de se rattacher à soi-même.
    if (targetManagerId !== UNMANAGED && targetManagerId === personId) {
      setError('Une personne ne peut pas être son propre manager.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (targetManagerId === UNMANAGED) {
        // Détacher du manager source uniquement.
        if (fromManagerId !== UNMANAGED) await apiPersonManagers.remove(personId, fromManagerId);
      } else {
        // Assign nouveau (avant remove pour ne jamais orphaniser), puis remove source.
        await apiPersonManagers.assign(personId, targetManagerId);
        if (fromManagerId !== UNMANAGED) await apiPersonManagers.remove(personId, fromManagerId);
      }
      await load(true);
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Retire un collaborateur de son manager (croix sur la ligne, hors drag&drop).
  const removeLink = async (personId, managerId) => {
    if (managerId === UNMANAGED) return;
    if (!window.confirm('Retirer ce collaborateur de ce manager ?')) return;
    setBusy(true);
    setError(null);
    try {
      await apiPersonManagers.remove(personId, managerId);
      await load(true);
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---------- Styles ----------
  const card = {
    background: 'var(--card-bg)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-card)',
    marginBottom: 14,
    overflow: 'hidden',
  };
  const headerStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    background: active ? 'var(--accent)' : 'var(--glass)',
    color: active ? '#fff' : 'var(--text)',
    borderBottom: '1px solid var(--glass-border)',
    transition: 'background 120ms',
  });
  const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '20px 1.6fr 1.2fr 1.2fr 70px',
    alignItems: 'center',
    gap: 10,
    padding: '8px 14px',
    borderTop: '1px solid var(--glass-border)',
    fontSize: 13,
    // Sans ça, un glisser sur le texte déclenche une sélection au lieu du drag.
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };
  const cellMuted = { color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const iconBtn = {
    background: 'transparent',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    padding: 4,
    display: 'inline-flex',
    alignItems: 'center',
  };

  const renderReportRow = (p, fromManagerId) => {
    if (!matches(p)) return null;
    return (
      <div
        key={`${fromManagerId}:${p.id}`}
        draggable={canEdit}
        onDragStart={(e) => onDragStart(e, p.id, fromManagerId)}
        onDragEnd={() => {
          setDrag(null);
          setDropTarget(null);
        }}
        style={{
          ...rowStyle,
          cursor: canEdit ? 'grab' : 'default',
          opacity: drag?.personId === p.id && drag?.fromManagerId === fromManagerId ? 0.4 : 1,
        }}
      >
        <span style={{ color: 'var(--text-muted)', display: 'inline-flex' }}>
          {canEdit ? <GripVertical size={16} /> : null}
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={() => onOpenPerson(p.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenPerson(p.id);
            }
          }}
          title={canOpenEdit ? 'Ouvrir la fiche (modifier)' : 'Ouvrir la fiche'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, cursor: 'pointer' }}
        >
          <Avatar person={{ firstName: p.firstName, lastName: p.lastName, avatarUrl: p.avatarUrl }} size={26} />
          <span style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>
              {p.lastName} {p.firstName}
            </span>
            {p.jobTitle ? <span style={{ ...cellMuted, fontSize: 11, display: 'block' }}>{p.jobTitle}</span> : null}
          </span>
        </span>
        <span style={cellMuted}>{p.roleName ?? '—'}</span>
        <span style={cellMuted}>{p.departmentName ?? '—'}</span>
        <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => onOpenPerson(p.id)} title={canOpenEdit ? 'Modifier' : 'Voir la fiche'} style={iconBtn}>
            {canOpenEdit ? <Pencil size={14} /> : <Eye size={14} />}
          </button>
          {canEdit && fromManagerId !== UNMANAGED ? (
            <button
              type="button"
              onClick={() => removeLink(p.id, fromManagerId)}
              title="Retirer de ce manager"
              style={{ ...iconBtn, color: 'var(--danger)' }}
            >
              <X size={14} />
            </button>
          ) : null}
        </span>
      </div>
    );
  };

  // ⚠ Appelée comme une FONCTION (renderSection(...)), pas comme <Section/> :
  // un composant défini inline serait recréé à chaque render et React
  // démonterait/remonterait la section pendant le drag (→ glisser annulé).
  const renderSection = ({ sectionKey, headerNode, reports, fromManagerId }) => {
    const active = dropTarget === sectionKey && drag && drag.fromManagerId !== sectionKey;
    const isCollapsed = collapsed.has(sectionKey);
    return (
      <div
        style={{ ...card, outline: active ? '2px dashed var(--accent)' : 'none' }}
        onDragOver={
          canEdit
            ? (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dropTarget !== sectionKey) setDropTarget(sectionKey);
              }
            : undefined
        }
        onDragLeave={canEdit ? (e) => {
          // Ne réinitialise que si on quitte réellement la carte (pas un enfant).
          if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget((t) => (t === sectionKey ? null : t));
        } : undefined}
        onDrop={canEdit ? () => onDrop(sectionKey) : undefined}
      >
        <div style={{ ...headerStyle(active), cursor: 'pointer' }} onClick={() => toggleCollapse(sectionKey)}>
          <span style={{ display: 'inline-flex', alignItems: 'center', color: active ? '#fff' : 'var(--text-muted)' }}>
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          </span>
          {headerNode}
        </div>
        {isCollapsed ? null : reports.length === 0 ? (
          <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
            {canEdit ? 'Déposez un collaborateur ici' : 'Aucun collaborateur'}
          </div>
        ) : (
          reports.map((p) => renderReportRow(p, fromManagerId))
        )}
      </div>
    );
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Chargement…</div>;

  return (
    <div style={{ padding: '0 4px 32px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <Search
            size={15}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtrer (nom, rôle, département…)"
            style={{
              width: '100%',
              padding: '8px 10px 8px 32px',
              background: 'var(--glass)',
              color: 'var(--text)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 13,
            }}
          />
        </div>
        {(() => {
          const allKeys = [UNMANAGED, ...sections.map((s) => s.managerId)];
          const pill = {
            background: 'transparent',
            color: 'var(--text)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-pill)',
            padding: '6px 12px',
            fontSize: 13,
            cursor: 'pointer',
          };
          return (
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button type="button" style={pill} onClick={() => setCollapsed(new Set(allKeys))}>
                Tout réduire
              </button>
              <button type="button" style={pill} onClick={() => setCollapsed(new Set())}>
                Tout déplier
              </button>
            </div>
          );
        })()}
        {canEdit ? (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Glissez un collaborateur d’un manager vers un autre pour le réaffecter.
          </span>
        ) : null}
        {busy ? <span style={{ fontSize: 13, color: 'var(--accent)' }}>Mise à jour…</span> : null}
      </div>

      {error ? <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div> : null}

      {/* Section "Sans manager" — utile comme source/cible de détachement. */}
      {renderSection({
        sectionKey: UNMANAGED,
        fromManagerId: UNMANAGED,
        reports: unmanaged,
        headerNode: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            <Users2 size={18} /> Sans manager <span style={{ opacity: 0.7 }}>({unmanaged.length})</span>
          </span>
        ),
      })}

      {/* Une section par manager. */}
      {sections.map((s) => (
        <div key={s.managerId}>
          {renderSection({
            sectionKey: s.managerId,
            fromManagerId: s.managerId,
            reports: s.reports,
            headerNode: (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenPerson(s.manager.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenPerson(s.manager.id);
                  }
                }}
                title={canOpenEdit ? 'Ouvrir la fiche (modifier)' : 'Ouvrir la fiche'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, cursor: 'pointer' }}
              >
                <Avatar
                  person={{ firstName: s.manager.firstName, lastName: s.manager.lastName, avatarUrl: s.manager.avatarUrl }}
                  size={28}
                />
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, color: 'var(--accent)' }}>
                    {s.manager.lastName} {s.manager.firstName}
                  </span>
                  <span style={{ display: 'block', fontSize: 11, opacity: 0.8 }}>
                    {s.reports.length} collaborateur{s.reports.length > 1 ? 's' : ''}
                    {s.manager.jobTitle ? ` · ${s.manager.jobTitle}` : ''}
                  </span>
                </span>
              </span>
            ),
          })}
        </div>
      ))}
    </div>
  );
}
